/**
 * ViewSettingsPanel.tsx - ViewSettingsPanel - Modular GUI editor for view CSS settings and profiles
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useEffect } from 'react';
import './ViewSettingsPanel.css';

/**
 * Schema format:
 * {
 *   groups: [
 *     {
 *       name: 'Typography',
 *       settings: [
 *         { key: 'fontSize', label: 'Font Size', type: 'text', default: '14px' },
 *         { key: 'lineHeight', label: 'Line Height', type: 'text', default: '1.5' },
 *       ]
 *     }
 *   ],
 *   cssGenerator: (settings) => string,  // Function to generate CSS from settings
 *   previewComponent: (settings) => JSX  // Optional custom preview
 * }
 */

const ViewSettingsPanel = ({ 
  viewType, 
  schema, 
  onClose, 
  onApply,
  storageKey = null  // Optional custom storage key
}) => {
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [settings, setSettings] = useState({});
  const [profileName, setProfileName] = useState('');

  const storageKeyName = storageKey || `viewProfiles_${viewType}`;

  // Extract default settings from schema
  const getDefaultSettings = () => {
    const defaults = { name: 'Default' };
    schema.groups.forEach(group => {
      group.settings.forEach(setting => {
        defaults[setting.key] = setting.default;
      });
    });
    return defaults;
  };

  // Load profiles from localStorage on mount
  useEffect(() => {
    const savedProfiles = localStorage.getItem(storageKeyName);
    if (savedProfiles) {
      try {
        const parsed = JSON.parse(savedProfiles);
        setProfiles(parsed);
        if (parsed.length > 0) {
          loadProfile(parsed[0]);
        }
      } catch (e) {
        console.error('Failed to load profiles:', e);
        initializeDefaultProfile();
      }
    } else {
      initializeDefaultProfile();
    }
  }, [viewType, storageKeyName]);

  const initializeDefaultProfile = () => {
    const defaultProfile = {
      ...getDefaultSettings(),
      id: 'default',
      name: 'Default'
    };
    setProfiles([defaultProfile]);
    loadProfile(defaultProfile);
  };

  const loadProfile = (profile) => {
    setCurrentProfile(profile);
    setSettings(profile);
    setProfileName(profile.name);
  };

  const saveProfiles = (newProfiles) => {
    localStorage.setItem(storageKeyName, JSON.stringify(newProfiles));
    setProfiles(newProfiles);
  };

  const handleSaveProfile = () => {
    const newProfile = {
      ...settings,
      id: currentProfile?.id || `profile_${Date.now()}`,
      name: profileName || 'Unnamed Profile',
    };

    const existingIndex = profiles.findIndex(p => p.id === newProfile.id);
    let newProfiles;
    
    if (existingIndex >= 0) {
      // Update existing
      newProfiles = [...profiles];
      newProfiles[existingIndex] = newProfile;
    } else {
      // Add new
      newProfiles = [...profiles, newProfile];
    }

    saveProfiles(newProfiles);
    setCurrentProfile(newProfile);
  };

  const handleNewProfile = () => {
    setSettings(getDefaultSettings());
    setProfileName('New Profile');
    setCurrentProfile(null);
  };

  const handleDeleteProfile = (profileId) => {
    if (profiles.length <= 1) {
      alert('Cannot delete the last profile');
      return;
    }
    
    const newProfiles = profiles.filter(p => p.id !== profileId);
    saveProfiles(newProfiles);
    
    if (currentProfile?.id === profileId) {
      loadProfile(newProfiles[0]);
    }
  };

  const handleDuplicateProfile = () => {
    const newProfile = {
      ...settings,
      id: `profile_${Date.now()}`,
      name: `${profileName} (Copy)`,
    };
    const newProfiles = [...profiles, newProfile];
    saveProfiles(newProfiles);
    loadProfile(newProfile);
  };

  const handleExportProfile = () => {
    const dataStr = JSON.stringify(currentProfile, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${viewType}_${currentProfile.name}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleImportProfile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        imported.id = `profile_${Date.now()}`;
        imported.name = `${imported.name} (Imported)`;
        
        const newProfiles = [...profiles, imported];
        saveProfiles(newProfiles);
        loadProfile(imported);
      } catch (err) {
        alert('Failed to import profile: Invalid file format');
      }
    };
    reader.readAsText(file);
  };

  const handleApply = () => {
    if (schema.cssGenerator) {
      const css = schema.cssGenerator(settings);
      if (onApply) {
        onApply(css, settings);
      }
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const renderSettingInput = (setting) => {
    const value = settings[setting.key] ?? setting.default;

    switch (setting.type) {
      case 'color':
        return (
          <div className="color-input">
            <input
              type="color"
              value={value}
              onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            />
            <input
              type="text"
              value={value}
              onChange={(e) => handleSettingChange(setting.key, e.target.value)}
              placeholder={setting.placeholder}
            />
          </div>
        );
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
          >
            {setting.options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            placeholder={setting.placeholder}
            min={setting.min}
            max={setting.max}
            step={setting.step}
          />
        );
      
      case 'checkbox':
        return (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
            />
            <span>{setting.description}</span>
          </label>
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            placeholder={setting.placeholder}
            rows={setting.rows || 3}
          />
        );
      
      case 'text':
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            placeholder={setting.placeholder}
          />
        );
    }
  };

  return (
    <>
      <div className="view-settings-backdrop" onClick={onClose} />
      <div className="view-settings-panel">
        <div className="panel-header">
          <h2>{schema.title || `${viewType} Settings`}</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="panel-content">
          {/* Profile Selector */}
          <div className="profile-section">
            <h3>Profiles</h3>
            <div className="profile-list">
              {profiles.map(profile => (
                <div 
                  key={profile.id}
                  className={`profile-item ${currentProfile?.id === profile.id ? 'active' : ''}`}
                >
                  <button 
                    onClick={() => loadProfile(profile)}
                    className="profile-name"
                    title={profile.name}
                  >
                    {profile.name}
                  </button>
                  <button 
                    onClick={() => handleDeleteProfile(profile.id)}
                    className="delete-profile"
                    disabled={profiles.length <= 1}
                    title="Delete profile"
                  >
                    🗑
                  </button>
                </div>
              ))}
            </div>
            
            <div className="profile-actions">
              <button onClick={handleNewProfile} className="new-profile-button">
                + New Profile
              </button>
              <button onClick={handleDuplicateProfile} className="action-button">
                📋 Duplicate
              </button>
              <button onClick={handleExportProfile} className="action-button">
                💾 Export
              </button>
              <label className="action-button import-button">
                📂 Import
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportProfile}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>

          {/* Settings Editor */}
          <div className="settings-section">
            <h3>Settings</h3>
            
            <div className="setting-group">
              <label>Profile Name</label>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="Profile name"
              />
            </div>

            {schema.groups.map(group => (
              <div key={group.name} className="settings-group">
                <h4>{group.name}</h4>
                {group.settings.map(setting => (
                  <div key={setting.key} className="setting-group">
                    <label>{setting.label}</label>
                    {renderSettingInput(setting)}
                    {setting.description && setting.type !== 'checkbox' && (
                      <span className="setting-description">{setting.description}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Preview */}
          <div className="preview-section">
            <h3>Preview</h3>
            <div className="preview-container">
              {schema.cssGenerator && (
                <style>{schema.cssGenerator(settings)}</style>
              )}
              {schema.previewComponent ? (
                schema.previewComponent(settings)
              ) : (
                <div className="default-preview">
                  <p>No preview available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="panel-footer">
          <button onClick={handleSaveProfile} className="save-button">
            💾 Save Profile
          </button>
          <button onClick={handleApply} className="apply-button">
            ✓ Apply to View
          </button>
          <button onClick={onClose} className="cancel-button">
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default ViewSettingsPanel;
